/**
 * Surface navigation (WORKBOARD lane D3): routes across a planet for anything that walks —
 * toward objectives, along cover and high ground, away from what can see it. Pure and
 * deterministic (same planet + obstacles + body + query → same route on every engine).
 *
 * **The graph.** Evenly spread points on the sphere (a Fibonacci lattice at the body's
 * spacing, capped in count), each lifted to the terrain. Edges join near neighbours and
 * obey the walk's own rules (core/surfaceWalk.ts stepWalk): an edge is dropped if any half
 * of it rises more steeply than the body can climb (downhill is always allowed, so edges are
 * directed) — or, for a body that scrambles (decision 134), kept at the scramble's slower pace —
 * if either end stands inside an obstacle (radius + the body's radius), or if its midpoint does. Built once per planet and body; the terrain or the obstacles changing means
 * a rebuild (`navGraphStale`).
 *
 * **The search.** A* over the graph. Distance is measured as angle × the planet's reference
 * radius, so the great-circle heuristic is an exact lower bound and every extra cost term is
 * ≥ 0 per unit of distance — the search stays optimal under any cost layer:
 *   - `slope`: uphill grade costs extra (a climb is slower and exposed on the skyline);
 *   - `threats`: what can see a spot — the guards' own perception geometry (range, a cone
 *     when facing is known, terrain line of sight; 1 seen, ½ in range but looking away,
 *     ¼ behind terrain) — the same rule as the sneaking walk's exposure;
 *   - `avoid`: soft keep-out discs (an enemy-held point, a hazard), cost falling to 0 at the rim;
 *   - `preferHigh`: low ground costs extra (the advantage of height).
 * Node costs are computed lazily, only for nodes the search reaches.
 *
 * **After the search.** `smoothRoute` pulls the string: a straight walk replaces a run of
 * nodes when the walk is legal (slope + obstacles sampled along it) and no costlier than the
 * worst node it replaces. `RouteCursor` / `nextWaypoint` follow a route from wherever the
 * walker actually is.
 */

import type { PlanetState, Vec3 } from '../types';
import { vecAdd, vecScale, vecSub, vecDot, vecNormalize, vecLength } from '../math';
import { dsin, dcos, dasin } from './dmath';
import { getSurfaceRadius, lastSurfaceTriangle } from './surface';
import { hasLineOfSight } from './los';
import { tangentToward } from './perception';
import { PERCEPTION_CONE_HALF_ANGLE } from '../constants';
import type { WalkObstacle } from './surfaceWalk';
import { buildCellIndex, queryCellIndexInto, cellsPerEdge, type CellIndex } from './cells';
import { FOREST } from './forest';

// -----------------------------------------------------------------------------
// Types
// -----------------------------------------------------------------------------

/** What walks: how wide it is, how steep it climbs, where its eyes are, how fine a grid it needs. */
export interface NavBody {
  unitRadius: number;
  maxSlope: number;
  eyeHeight: number;
  /** Decision 134: the share of the pace it keeps scrambling up ground too steep to walk. Such an edge is kept, at that
   * pace (its length × √(1 + grade²) ÷ this); absent or 0, it is dropped as before. */
  scramble?: number;
  /** Wanted node spacing (u); default 4 × unitRadius. The count cap may widen it. */
  spacing?: number;
  /** 63.5 step 4 (with ground materials): the ground's footing at a direction (core/groundWalk.ts routeFooting) — an edge's
   * pace ÷ its ground's speed (and its uphill loss), its scramble × the ground's: a route prefers firm ground. Absent: as before. */
  ground?: (dir: Vec3) => { speed: number; scramble: number; uphill: number };
}

/** Most nodes one planet's graph may have (the spacing widens to fit). */
export const NAV_MAX_NODES = 40_000;
/** Neighbours are joined within this many lattice spacings (1.6 ≈ 6–7 edges a node; longer reach barely
 * straightens raw routes — 1.10 → 1.05 stretch at 4× the edges — and smoothing straightens them anyway). */
const NEIGHBOUR_REACH = 1.6;
/**
 * Routes climb at most this fraction of the body's max slope. Grades are sampled (a quarter of a
 * terrain-mesh edge apart); the walk checks every tick, so a little margin keeps a sampled-legal
 * route legal for the walk.
 */
export const NAV_SLOPE_MARGIN = 0.9;

/** Terrain mesh resolution as an arc (u): non-indexed triangles, ~6 corners per unique vertex. */
function meshSpacing(planet: PlanetState): number {
  const unique = Math.max(12, planet.heightmap.length / 6);
  return planet.radius * Math.sqrt(4 * Math.PI / unique);
}

/** Deepest bisection when looking for the slope between two terrain triangles. */
const MAX_BISECT = 14;

/**
 * The steepest uphill grade walking a→b and walking b→a along the great circle, as the walk
 * meets it (it checks every tick). The terrain is flat triangles, so the grade only changes
 * where the path crosses a triangle edge: sample every `step` (u), and wherever two samples
 * fall in different triangles, bisect down to `fine` (u) — a short steep sliver at a corner
 * cannot average away. Returns [up a→b, up b→a].
 */
function sampledGrades(planet: PlanetState, a: Vec3, b: Vec3, step: number, fine: number): [number, number] {
  const total = angleBetween(a, b);
  if (total < 1e-12) return [0, 0];
  const ua = vecNormalize(a), ub = vecNormalize(b);
  const axis = vecNormalize(vecSub(ub, vecScale(ua, vecDot(ua, ub))));
  const pointAt = (t: number) => t >= total ? ub : (t <= 0 ? ua : vecNormalize(vecAdd(vecScale(ua, dcos(t)), vecScale(axis, dsin(t)))));
  const sample = (t: number): [number, number] => { const h = getSurfaceRadius(planet, pointAt(t)); return [h, lastSurfaceTriangle()]; };
  const fineAng = fine / planet.radius;
  let up = 0, down = 0;
  const account = (h0: number, h1: number, dt: number) => {
    const g = (h1 - h0) / (dt * Math.min(h0, h1));
    if (g > up) up = g;
    if (-g > down) down = -g;
  };
  const span = (t0: number, h0: number, tri0: number, t1: number, h1: number, tri1: number, depth: number): void => {
    if ((tri0 === tri1 && tri0 >= 0) || t1 - t0 <= fineAng || depth >= MAX_BISECT) { account(h0, h1, t1 - t0); return; }
    const tm = (t0 + t1) / 2;
    const [hm, trim] = sample(tm);
    span(t0, h0, tri0, tm, hm, trim, depth + 1);
    span(tm, hm, trim, t1, h1, tri1, depth + 1);
  };
  const n = Math.max(2, Math.ceil(total * planet.radius / step));
  let [h0, tri0] = sample(0);
  let t0 = 0;
  for (let k = 1; k <= n; k++) {
    const t1 = total * k / n;
    const [h1, tri1] = sample(t1);
    span(t0, h0, tri0, t1, h1, tri1, 0);
    t0 = t1; h0 = h1; tri0 = tri1;
  }
  return [up, down];
}

export interface NavGraph {
  planetIndex: number;
  planet: PlanetState;
  /** Reference radius: distances are angle × this. */
  radius: number;
  body: NavBody;
  /** Lattice spacing actually used (u, at the reference radius). */
  spacing: number;
  /** Slope sampling step (u) and the steepest grade a route may climb. */
  sampleStep: number;
  fineStep: number;
  climb: number;
  count: number;
  /** Unit directions, xyz per node. */
  dirs: Float64Array;
  /** Terrain radius at each node. */
  heights: Float64Array;
  /** 1 = inside an obstacle (no edges). */
  blocked: Uint8Array;
  /** Directed edges, CSR: node i's edges are edgeTo[edgeStart[i] .. edgeStart[i+1]). */
  edgeStart: Int32Array;
  edgeTo: Int32Array;
  /** Edge length (angle × radius). */
  edgeLen: Float64Array;
  /** Edge uphill grade (rise over run, ≥ 0; the steeper half). */
  edgeGrade: Float64Array;
  /** Decision 134: the time an edge takes per unit of its length — 1 walked, a scramble's √(1 + grade²) ÷ scramble (≥ 1,
   * so the great-circle heuristic stays a lower bound). */
  edgePace: Float64Array;
  heightMin: number;
  heightMax: number;
  /** For staleness checks. */
  heightmap: Float32Array;
  obstacleCount: number;
  // the nodes filed by cell (core/cells.ts), and the neighbour reach (rad) an edge spans at most
  nodeIndex: CellIndex;
  reach: number;
  // obstacles, for segment checks: their unit directions, filed by cell, and the widest keep-out (rad, body included)
  obstacles: readonly WalkObstacle[];
  obstacleDirs: Vec3[];
  obstacleIndex: CellIndex;
  obstacleReach: number;
  // search workspace (reused; single-threaded)
  ws: { stamp: Int32Array; gen: number; g: Float64Array; parent: Int32Array; closed: Int32Array; costStamp: Int32Array; nodeCost: Float64Array };
}

/** Something that can see: an eye point, how far it sees, and (when known) which way it faces. */
export interface NavThreat {
  eye: Vec3;
  range: number;
  /** Unit tangent the threat faces; omitted = looks everywhere. */
  facing?: Vec3;
  /** Outward normal at the threat; required with `facing`. */
  normal?: Vec3;
}

export interface NavAvoid { dir: Vec3; /** u */ radius: number; weight: number }

/** Extra cost per unit of distance walked, by layer. Every term is ≥ 0. */
export interface RouteCost {
  /** × uphill grade (default 1). */
  slope?: number;
  threats?: readonly NavThreat[];
  /** × exposure (default 2). */
  threatWeight?: number;
  /** Planets for the threats' line of sight (required with threats). */
  planets?: readonly PlanetState[];
  avoid?: readonly NavAvoid[];
  /** × how low a node is (0 at the graph's highest node, 1 at its lowest). */
  preferHigh?: number;
  /**
   * A caller-held memo of node field costs, valid while this cost's inputs are unchanged (the
   * same threats, avoid discs and weights). A walker that replans often against the same
   * sighting reuses its line-of-sight answers instead of paying for them every plan.
   */
  memo?: Map<number, number>;
}

export interface RouteOptions {
  /** Stop when within this arc (u) of the goal (default: half a spacing — the nearest free node). */
  goalRadius?: number;
  /** Give up after expanding this many nodes (default: the whole graph). */
  maxExpand?: number;
  /** Heuristic multiplier (default 1 = optimal A*; 0 = Dijkstra; tests compare them). */
  heuristic?: number;
}

export interface Route {
  /** Surface directions from the start to the goal, start first. */
  points: Vec3[];
  /** Node indices along the route (before smoothing; empty after). */
  nodes: number[];
  /** Distance walked (angle × radius). */
  length: number;
  /** Search cost (distance + the layers). */
  cost: number;
  /** Exposure × distance along the route (0 without threats). */
  exposure: number;
  expanded: number;
}

// -----------------------------------------------------------------------------
// Small geometry
// -----------------------------------------------------------------------------

const clamp = (x: number, lo: number, hi: number) => Math.max(lo, Math.min(hi, x));

/** Angle between unit vectors, accurate for small angles (chord-based). */
export function angleBetween(a: Vec3, b: Vec3): number {
  const dx = a.x - b.x, dy = a.y - b.y, dz = a.z - b.z;
  return 2 * dasin(clamp(Math.sqrt(dx * dx + dy * dy + dz * dz) / 2, 0, 1));
}

function dirAt(g: NavGraph, i: number): Vec3 { return { x: g.dirs[3 * i], y: g.dirs[3 * i + 1], z: g.dirs[3 * i + 2] }; }

function angleNodes(g: NavGraph, i: number, j: number): number {
  const d = g.dirs;
  const dx = d[3 * i] - d[3 * j], dy = d[3 * i + 1] - d[3 * j + 1], dz = d[3 * i + 2] - d[3 * j + 2];
  return 2 * dasin(clamp(Math.sqrt(dx * dx + dy * dy + dz * dz) / 2, 0, 1));
}

/** A query cap widened a hair past `angle`, so the cells' superset never misses an item the exact test keeps. */
const capOf = (angle: number) => angle * (1 + 1e-9) + 1e-12;

/** Whether a body standing at `dir` (unit) overlaps an obstacle: the angle to its centre × radius under its radius + the body's. */
function inObstacle(g: Pick<NavGraph, 'obstacles' | 'obstacleDirs' | 'obstacleIndex' | 'obstacleReach' | 'radius' | 'body'>, dir: Vec3, near: number[]): boolean {
  if (!g.obstacles.length) return false;
  queryCellIndexInto(g.obstacleIndex, dir, capOf(g.obstacleReach), near);
  for (const oi of near) {
    if (angleBetween(dir, g.obstacleDirs[oi]) * g.radius < g.obstacles[oi].radius + g.body.unitRadius) return true;
  }
  return false;
}

// -----------------------------------------------------------------------------
// Build
// -----------------------------------------------------------------------------

/** The spacing a body gets on a planet: its own, widened to fit the node cap. */
export function navSpacing(radius: number, body: NavBody): number {
  const want = body.spacing ?? body.unitRadius * 4;
  const floor = radius * Math.sqrt(4 * Math.PI / NAV_MAX_NODES);
  return Math.max(want, floor);
}

/** A navigation graph for one planet, one body, the obstacles standing on it. */
export function buildNavGraph(planet: PlanetState, planetIndex: number, obstacles: readonly WalkObstacle[], body: NavBody): NavGraph {
  const radius = planet.radius;
  const spacing = navSpacing(radius, body);
  const count = Math.max(12, Math.min(NAV_MAX_NODES, Math.ceil(4 * Math.PI * radius * radius / (spacing * spacing))));
  const ang = Math.sqrt(4 * Math.PI / count);           // lattice spacing as an angle
  const reach = ang * NEIGHBOUR_REACH;

  // Fibonacci lattice
  const dirs = new Float64Array(3 * count);
  const golden = Math.PI * (3 - Math.sqrt(5));
  const TWO_PI = 2 * Math.PI;
  for (let i = 0; i < count; i++) {
    const z = 1 - (2 * i + 1) / count;
    const r = Math.sqrt(Math.max(0, 1 - z * z));
    const th = (i * golden) % TWO_PI;
    dirs[3 * i] = r * dcos(th); dirs[3 * i + 1] = r * dsin(th); dirs[3 * i + 2] = z;
  }

  // The nodes filed by cell (core/cells.ts), cells about a neighbour reach across
  const nodeIndex = buildCellIndex(count, i => ({ x: dirs[3 * i], y: dirs[3 * i + 1], z: dirs[3 * i + 2] }), cellsPerEdge(radius, reach * radius));

  // Heights
  const heights = new Float64Array(count);
  let heightMin = Infinity, heightMax = -Infinity;
  for (let i = 0; i < count; i++) {
    const h = getSurfaceRadius(planet, { x: dirs[3 * i], y: dirs[3 * i + 1], z: dirs[3 * i + 2] });
    heights[i] = h;
    if (h < heightMin) heightMin = h;
    if (h > heightMax) heightMax = h;
  }

  // Obstacles: their centres filed by cell, and the nodes they cover
  let obstacleReach = 0;
  for (const o of obstacles) obstacleReach = Math.max(obstacleReach, (o.radius + body.unitRadius) / radius);
  const obstacleDirs = obstacles.map(o => vecNormalize(o.dir));
  const obstacleIndex = buildCellIndex(obstacles.length, i => obstacleDirs[i], cellsPerEdge(radius, Math.max(obstacleReach, ang) * radius));
  const obs = { obstacles, obstacleDirs, obstacleIndex, obstacleReach, radius, body };
  const near: number[] = [];
  const blocked = new Uint8Array(count);
  for (let i = 0; i < count; i++) if (inObstacle(obs, dirAt({ dirs } as NavGraph, i), near)) blocked[i] = 1;

  // Edges (directed; computed once per pair)
  const sampleStep = Math.min(ang * radius / 2, meshSpacing(planet) / 4);
  const climb = body.maxSlope * NAV_SLOPE_MARGIN;
  // the walk's own step at a brisk pace: an eighth of the body's radius
  const fineStep = Math.max(1e-4, body.unitRadius / 8);
  const outs: Array<Array<{ to: number; len: number; grade: number; pace: number }>> = Array.from({ length: count }, () => []);
  const scramble = body.scramble ?? 0;
  const paceOf = (grade: number): number => Math.max(1, Math.sqrt(1 + grade * grade) / scramble);   // (a scramble's)
  const cand: number[] = [];
  for (let i = 0; i < count; i++) {
    if (blocked[i]) continue;
    const di = { x: dirs[3 * i], y: dirs[3 * i + 1], z: dirs[3 * i + 2] };
    queryCellIndexInto(nodeIndex, di, capOf(reach), cand);   // any order: each list is sorted below
    for (const j of cand) {
      if (j <= i || blocked[j]) continue;
      const dj = { x: dirs[3 * j], y: dirs[3 * j + 1], z: dirs[3 * j + 2] };
      const a = angleBetween(di, dj);
      if (a > reach || a < 1e-12) continue;
      const mid = vecNormalize(vecAdd(di, dj));
      if (inObstacle(obs, mid, near)) continue;
      // grades sampled at the terrain's own resolution, both ways in one pass
      const [gradeIJ, gradeJI] = sampledGrades(planet, di, dj, sampleStep, fineStep);
      const len = a * radius;
      const gw = body.ground ? body.ground(mid) : null;
      if (gw) {
        // 63.5 step 4: the ground's cost — slow ground, a hill of sand, a scramble on rock cost more
        const walkPace = (grade: number) => 1 / (gw.speed * Math.max(0.2, 1 - gw.uphill * Math.max(0, grade)));
        const scramblePace = (grade: number) => Math.max(1, Math.sqrt(1 + grade * grade) / (scramble * gw.scramble)) / gw.speed;
        if (gradeIJ <= climb) outs[i].push({ to: j, len, grade: gradeIJ, pace: walkPace(gradeIJ) });
        else if (scramble > 0) outs[i].push({ to: j, len, grade: gradeIJ, pace: scramblePace(gradeIJ) });
        if (gradeJI <= climb) outs[j].push({ to: i, len, grade: gradeJI, pace: walkPace(gradeJI) });
        else if (scramble > 0) outs[j].push({ to: i, len, grade: gradeJI, pace: scramblePace(gradeJI) });
        continue;
      }
      if (gradeIJ <= climb) outs[i].push({ to: j, len, grade: gradeIJ, pace: 1 });
      else if (scramble > 0) outs[i].push({ to: j, len, grade: gradeIJ, pace: paceOf(gradeIJ) });
      if (gradeJI <= climb) outs[j].push({ to: i, len, grade: gradeJI, pace: 1 });
      else if (scramble > 0) outs[j].push({ to: i, len, grade: gradeJI, pace: paceOf(gradeJI) });
    }
  }
  let total = 0;
  for (const o of outs) total += o.length;
  const edgeStart = new Int32Array(count + 1);
  const edgeTo = new Int32Array(total);
  const edgeLen = new Float64Array(total);
  const edgeGrade = new Float64Array(total);
  const edgePace = new Float64Array(total);
  let e = 0;
  for (let i = 0; i < count; i++) {
    edgeStart[i] = e;
    const list = outs[i].sort((p, q) => p.to - q.to);
    for (const x of list) { edgeTo[e] = x.to; edgeLen[e] = x.len; edgeGrade[e] = x.grade; edgePace[e] = x.pace; e++; }
  }
  edgeStart[count] = e;

  return {
    planetIndex, planet, radius, body, spacing: ang * radius, sampleStep, fineStep, climb, count, dirs, heights, blocked,
    edgeStart, edgeTo, edgeLen, edgeGrade, edgePace, heightMin, heightMax,
    heightmap: planet.heightmap, obstacleCount: obstacles.length,
    nodeIndex, reach, obstacles, obstacleDirs, obstacleIndex, obstacleReach,
    ws: {
      stamp: new Int32Array(count), gen: 0, g: new Float64Array(count), parent: new Int32Array(count),
      closed: new Int32Array(count), costStamp: new Int32Array(count), nodeCost: new Float64Array(count),
    },
  };
}

/** Whether a graph no longer matches its planet (terrain changed) or the obstacle list. */
/**
 * E5.4 step 3 (ARCHITECTURE §5.17): the obstacles a nav graph is built over — the walk's, less the planet forest's trees
 * (ids from FOREST.idBase, core/forest.ts). The forest's cells join and leave the sim as units move (step 4); in the
 * graph they would rebuild it (0.8–1.5 s: its staleness is the obstacles' count) at every join. Out of it, a bot routes
 * over the lattice and steers round those trees as it walks, as it does round any obstacle between two nodes. The same
 * array while the walk's is the same and holds no forest tree (today's battles: nothing changes).
 */
const navObstacleMemo = new WeakMap<readonly WalkObstacle[], readonly WalkObstacle[]>();
const isForestTree = (o: WalkObstacle) => o.ref?.kind === 'flora' && o.ref.id >= FOREST.idBase;
export function navObstacles(obstacles: readonly WalkObstacle[]): readonly WalkObstacle[] {
  let kept = navObstacleMemo.get(obstacles);
  if (!kept) { kept = obstacles.some(isForestTree) ? obstacles.filter(o => !isForestTree(o)) : obstacles; navObstacleMemo.set(obstacles, kept); }
  return kept;
}

export function navGraphStale(g: NavGraph, planet: PlanetState, obstacles: readonly WalkObstacle[]): boolean {
  return g.heightmap !== planet.heightmap || g.obstacleCount !== obstacles.length;
}

/** The node nearest a direction (blocked or unconnected ones only if `allowBlocked`); ties go to the lower index. -1 if none. */
export function nearestNode(g: NavGraph, dir: Vec3, allowBlocked = false): number {
  const d = vecNormalize(dir);
  const cand: number[] = [];
  let best = -1, bestA = Infinity;
  const consider = (i: number) => {
    if (!allowBlocked && (g.blocked[i] || g.edgeStart[i] === g.edgeStart[i + 1])) return;
    const a = angleBetween(d, dirAt(g, i));
    if (a < bestA || (a === bestA && i < best)) { bestA = a; best = i; }
  };
  // a widening cap: once the nearest candidate lies inside it, nothing outside can be nearer
  for (let cap = g.reach; cap < Math.PI / 4; cap *= 4) {
    queryCellIndexInto(g.nodeIndex, d, capOf(cap), cand);
    for (const i of cand) consider(i);
    if (best >= 0 && bestA <= cap) return best;
  }
  // deep inside a wide obstacle field (or the graph is empty): scan everything
  for (let i = 0; i < g.count; i++) consider(i);
  return best;
}

// -----------------------------------------------------------------------------
// Costs
// -----------------------------------------------------------------------------

/** Where a walker's eyes are standing at a direction on this graph's planet. */
export function navEye(g: NavGraph, dir: Vec3, height?: number): Vec3 {
  const d = vecNormalize(dir);
  const h = (height ?? getSurfaceRadius(g.planet, d)) + g.body.eyeHeight;
  return vecAdd(g.planet.position, vecScale(d, h));
}

/** How exposed an eye point is: 1 per threat that sees it, ½ in range but looking away, ¼ behind terrain. */
export function exposureOf(eye: Vec3, threats: readonly NavThreat[], planets: readonly PlanetState[]): number {
  let total = 0;
  const cosHalf = dcos(PERCEPTION_CONE_HALF_ANGLE);
  for (const t of threats) {
    const dist = vecLength(vecSub(eye, t.eye));
    if (dist > t.range) continue;
    if (t.facing && t.normal) {
      const toward = tangentToward(t.normal, t.eye, eye);
      if (toward && vecDot(vecNormalize(t.facing), toward) < cosHalf) { total += 0.5; continue; }
    }
    total += hasLineOfSight(t.eye, eye, planets as PlanetState[]) ? 1 : 0.25;
  }
  return total;
}

/** The extra cost per unit distance of standing on node i, under a cost (no slope; that is per edge). */
function nodeFieldCost(g: NavGraph, i: number, cost: RouteCost, exposureOut?: { v: number }): number {
  let c = 0;
  if (cost.threats && cost.threats.length && cost.planets) {
    const ex = exposureOf(navEye(g, dirAt(g, i), g.heights[i]), cost.threats, cost.planets);
    if (exposureOut) exposureOut.v = ex;
    c += (cost.threatWeight ?? 2) * ex;
  }
  if (cost.avoid) {
    const d = dirAt(g, i);
    for (const a of cost.avoid) {
      const arc = angleBetween(d, vecNormalize(a.dir)) * g.radius;
      if (arc < a.radius) c += a.weight * (1 - arc / a.radius);
    }
  }
  if (cost.preferHigh) {
    const span = g.heightMax - g.heightMin;
    if (span > 1e-9) c += cost.preferHigh * (g.heightMax - g.heights[i]) / span;
  }
  return c;
}

/** Field cost of node i (lazily, memoised for the current search generation). */
function fieldCost(g: NavGraph, i: number, cost: RouteCost): number {
  const ws = g.ws;
  if (ws.costStamp[i] === ws.gen) return ws.nodeCost[i];
  let c: number;
  if (cost.memo) {
    const m = cost.memo.get(i);
    if (m !== undefined) c = m;
    else { c = nodeFieldCost(g, i, cost); cost.memo.set(i, c); }
  } else c = nodeFieldCost(g, i, cost);
  ws.costStamp[i] = ws.gen; ws.nodeCost[i] = c;
  return c;
}

/** Public: the field cost of standing at a direction (nearest node), for tests and tools. */
export function costAt(g: NavGraph, dir: Vec3, cost: RouteCost): number {
  const i = nearestNode(g, dir, true);
  return i < 0 ? 0 : nodeFieldCost(g, i, cost);
}

// -----------------------------------------------------------------------------
// Search
// -----------------------------------------------------------------------------

/** A binary heap of (f, node), ties broken by node index — deterministic. */
class Heap {
  f: number[] = []; n: number[] = [];
  get size() { return this.n.length; }
  private less(i: number, j: number) { return this.f[i] < this.f[j] || (this.f[i] === this.f[j] && this.n[i] < this.n[j]); }
  private swap(i: number, j: number) { const tf = this.f[i]; this.f[i] = this.f[j]; this.f[j] = tf; const tn = this.n[i]; this.n[i] = this.n[j]; this.n[j] = tn; }
  push(f: number, node: number) {
    this.f.push(f); this.n.push(node);
    let i = this.n.length - 1;
    while (i > 0) { const p = (i - 1) >> 1; if (!this.less(i, p)) break; this.swap(i, p); i = p; }
  }
  pop(): number {
    const top = this.n[0];
    const lf = this.f.pop()!, ln = this.n.pop()!;
    if (this.n.length > 0) {
      this.f[0] = lf; this.n[0] = ln;
      let i = 0;
      for (;;) {
        const l = 2 * i + 1, r = l + 1;
        let m = i;
        if (l < this.n.length && this.less(l, m)) m = l;
        if (r < this.n.length && this.less(r, m)) m = r;
        if (m === i) break;
        this.swap(i, m); i = m;
      }
    }
    return top;
  }
}

/**
 * The cheapest route from `from` to `to` under `cost`, or null (unreachable, or the
 * expansion budget ran out). Both ends snap to the nearest free node; the route's first
 * and last points are the exact `from` and `to` directions.
 */
export function findRoute(g: NavGraph, from: Vec3, to: Vec3, cost: RouteCost = {}, opts: RouteOptions = {}): Route | null {
  const start = nearestNode(g, from);
  const goalDir = vecNormalize(to);
  const goal = nearestNode(g, goalDir);
  if (start < 0 || goal < 0) return null;
  const ws = g.ws;
  ws.gen++;
  if (ws.gen > 2_000_000_000) { ws.stamp.fill(0); ws.closed.fill(0); ws.costStamp.fill(0); ws.gen = 1; }
  const gen = ws.gen;
  const slopeW = cost.slope ?? 1;
  const hw = opts.heuristic ?? 1;
  const goalRadius = opts.goalRadius ?? 0;
  const maxExpand = opts.maxExpand ?? g.count;
  const goalVec = dirAt(g, goal);
  const h = (i: number) => hw * angleNodes(g, i, goal) * g.radius;
  const isGoal = (i: number) => i === goal || (goalRadius > 0 && angleBetween(dirAt(g, i), goalDir) * g.radius <= goalRadius);

  const heap = new Heap();
  ws.stamp[start] = gen; ws.g[start] = 0; ws.parent[start] = -1;
  heap.push(h(start), start);
  let expanded = 0;
  let reached = -1;
  while (heap.size > 0) {
    const i = heap.pop();
    if (ws.closed[i] === gen) continue;
    ws.closed[i] = gen;
    expanded++;
    if (isGoal(i)) { reached = i; break; }
    if (expanded >= maxExpand) break;
    const gi = ws.g[i];
    for (let e = g.edgeStart[i]; e < g.edgeStart[i + 1]; e++) {
      const j = g.edgeTo[e];
      if (ws.closed[j] === gen) continue;
      const len = g.edgeLen[e];
      const step = len * (g.edgePace[e] + slopeW * g.edgeGrade[e] + fieldCost(g, j, cost));
      const gj = gi + step;
      if (ws.stamp[j] !== gen || gj < ws.g[j]) {
        ws.stamp[j] = gen; ws.g[j] = gj; ws.parent[j] = i;
        heap.push(gj + h(j), j);
      }
    }
  }
  if (reached < 0) return null;
  void goalVec;

  const nodes: number[] = [];
  for (let i = reached; i >= 0; i = ws.parent[i]) nodes.push(i);
  nodes.reverse();
  const points = [vecNormalize(from), ...nodes.map(i => dirAt(g, i)), goalDir];
  let length = 0, exposure = 0;
  const ex = { v: 0 };
  for (let k = 1; k < points.length; k++) length += angleBetween(points[k - 1], points[k]) * g.radius;
  if (cost.threats && cost.threats.length && cost.planets) {
    for (let k = 1; k < nodes.length; k++) {
      nodeFieldCost(g, nodes[k], { threats: cost.threats, planets: cost.planets }, ex);
      exposure += ex.v * angleNodes(g, nodes[k - 1], nodes[k]) * g.radius;
    }
  }
  return { points, nodes, length, cost: ws.g[reached], exposure, expanded };
}

/**
 * The cheapest cost from `from` to every node within `maxCost` (Dijkstra), for picking the
 * best spot in reach — the nearest cover, the highest ground, the least exposed corner.
 * Returns node → cost for the nodes reached, in node order.
 */
export function reachable(g: NavGraph, from: Vec3, maxCost: number, cost: RouteCost = {}): Map<number, number> {
  const start = nearestNode(g, from);
  const out = new Map<number, number>();
  if (start < 0) return out;
  const ws = g.ws;
  ws.gen++;
  const gen = ws.gen;
  const slopeW = cost.slope ?? 1;
  const heap = new Heap();
  ws.stamp[start] = gen; ws.g[start] = 0;
  heap.push(0, start);
  while (heap.size > 0) {
    const i = heap.pop();
    if (ws.closed[i] === gen) continue;
    ws.closed[i] = gen;
    const gi = ws.g[i];
    if (gi > maxCost) break;
    out.set(i, gi);
    for (let e = g.edgeStart[i]; e < g.edgeStart[i + 1]; e++) {
      const j = g.edgeTo[e];
      if (ws.closed[j] === gen) continue;
      const gj = gi + g.edgeLen[e] * (g.edgePace[e] + slopeW * g.edgeGrade[e] + fieldCost(g, j, cost));
      if (ws.stamp[j] !== gen || gj < ws.g[j]) { ws.stamp[j] = gen; ws.g[j] = gj; heap.push(gj, j); }
    }
  }
  return new Map([...out].sort((a, b) => a[0] - b[0]));
}

/** The direction of node i (for callers holding node ids from `reachable`). */
export function nodeDir(g: NavGraph, i: number): Vec3 { return dirAt(g, i); }

// -----------------------------------------------------------------------------
// Smoothing and following
// -----------------------------------------------------------------------------

/** Whether a straight walk from a to b is legal for the graph's body: slope and obstacles, sampled. */
export function segmentWalkable(g: NavGraph, a: Vec3, b: Vec3): boolean {
  const total = angleBetween(a, b);
  if (total < 1e-12) return true;
  if (sampledGrades(g.planet, a, b, g.sampleStep, g.fineStep)[0] > g.climb) return false;
  if (!g.obstacles.length) return true;
  const steps = Math.max(1, Math.ceil(total * g.radius / g.sampleStep));
  const near: number[] = [];
  const ua = vecNormalize(a);
  const axis = vecNormalize(vecSub(vecNormalize(b), vecScale(ua, vecDot(ua, vecNormalize(b)))));
  for (let s = 1; s <= steps; s++) {
    const t = total * s / steps;
    const p = vecNormalize(vecAdd(vecScale(ua, dcos(t)), vecScale(axis, dsin(t))));
    if (inObstacle(g, p, near)) return false;
  }
  return true;
}

/**
 * Pull the string: replace runs of route points by straight walks where the walk is legal
 * and no costlier (under `cost`'s field) than the worst point it replaces. The ends stay.
 */
export function smoothRoute(g: NavGraph, route: Route, cost: RouteCost = {}): Route {
  const pts = route.points;
  if (pts.length <= 2) return route;
  const fc = (p: Vec3) => (cost.threats?.length || cost.avoid?.length || cost.preferHigh) ? costAt(g, p, cost) : 0;
  const pc = pts.map(fc);
  const out: Vec3[] = [pts[0]];
  let i = 0;
  while (i < pts.length - 1) {
    let j = pts.length - 1;
    for (; j > i + 1; j--) {
      if (!segmentWalkable(g, pts[i], pts[j])) continue;
      let worst = 0;
      for (let k = i + 1; k < j; k++) worst = Math.max(worst, pc[k]);
      // sample the shortcut's field cost at a few points
      let ok = true;
      if (worst > 0 || pc.some(c => c > 0)) {
        const n = Math.max(2, Math.ceil(angleBetween(pts[i], pts[j]) * g.radius / g.spacing));
        for (let s = 1; s < n && ok; s++) {
          const t = s / n;
          const p = vecNormalize(vecAdd(vecScale(pts[i], 1 - t), vecScale(pts[j], t)));
          if (fc(p) > worst + 1e-9) ok = false;
        }
      }
      if (ok) break;
    }
    out.push(pts[j]);
    i = j;
  }
  let length = 0;
  for (let k = 1; k < out.length; k++) length += angleBetween(out[k - 1], out[k]) * g.radius;
  return { ...route, points: out, nodes: [], length };
}

/** Following a route: the index of the next point to walk to. */
export interface RouteCursor { points: Vec3[]; index: number }

export function routeCursor(route: Route): RouteCursor { return { points: route.points, index: Math.min(1, route.points.length - 1) }; }

/**
 * The point to walk toward now, from where the walker actually is: advances past points
 * within `reach` (u), and skips ahead when a later point (up to three on) is nearer — a
 * walker pushed off the route rejoins it forward, never backward. With the graph given, it
 * only moves on to a point the walker can walk to *from where it stands* (a smoothed route
 * hugs corners; cutting one from a little off the line can meet ground too steep).
 */
export function nextWaypoint(c: RouteCursor, pos: Vec3, radius: number, reach: number, g?: NavGraph): Vec3 {
  const p = vecNormalize(pos);
  const last = c.points.length - 1;
  const canWalk = (k: number) => !g || segmentWalkable(g, p, c.points[k]);
  for (let k = Math.min(last, c.index + 3); k > c.index; k--) {
    if (angleBetween(p, c.points[k]) < angleBetween(p, c.points[c.index]) && canWalk(k)) { c.index = k; break; }
  }
  while (c.index < last && angleBetween(p, c.points[c.index]) * radius < reach) {
    // at the point itself the next leg was checked when the route was made; near it, check from here
    const at = angleBetween(p, c.points[c.index]) * radius < reach * 0.1;
    if (!at && !canWalk(c.index + 1)) break;
    c.index++;
  }
  return c.points[c.index];
}

/** Whether the walker has reached the route's end. */
export function routeDone(c: RouteCursor, pos: Vec3, radius: number, reach: number): boolean {
  return c.index >= c.points.length - 1 && angleBetween(vecNormalize(pos), c.points[c.points.length - 1]) * radius < reach;
}
